import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  TablePagination
} from '@mui/material';
import {
  ArrowBack,
  Search,
  CheckCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://smartvidesa.lk/api';

const PaymentHistory = () => {
  const navigate = useNavigate();
  const [studentId, setStudentId] = useState('');
  const [studentInfo, setStudentInfo] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const searchStudent = async () => {
    if (!studentId) {
      setAlert({ show: true, message: 'Please enter student ID', severity: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Find student
      const studentsResponse = await axios.get(`${API_URL}/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const student = studentsResponse.data.find(s => s.student_id === studentId);
      
      if (!student) {
        setAlert({ show: true, message: 'Student not found', severity: 'error' });
        setStudentInfo(null);
        setPayments([]);
        return;
      }

      setStudentInfo(student);

      // Get payment history
      const paymentsResponse = await axios.get(`${API_URL}/fees/student/${student.id}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPayments(paymentsResponse.data || []);
      
      if (paymentsResponse.data.length === 0) {
        setAlert({ show: true, message: 'No payment history found', severity: 'info' });
      }
    } catch (error) {
      setAlert({ show: true, message: 'Error loading payment history', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getTotalPaid = () => {
    return payments
      .filter(p => p.payment_status === 'paid')
      .reduce((sum, p) => sum + parseFloat(p.amount), 0);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={() => navigate('/fund-collector/dashboard')}
            sx={{ color: 'white', mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography
              variant="h4"
              sx={{
                color: 'white',
                fontWeight: 700,
                mb: 1
              }}
            >
              Payment History
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              View complete payment history for any student
            </Typography>
          </Box>
        </Box>

        {/* Alert */}
        {alert.show && (
          <Alert
            severity={alert.severity}
            onClose={() => setAlert({ ...alert, show: false })}
            sx={{ mb: 3 }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Search Section */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Search Student
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Student ID"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                placeholder="Enter student ID"
                onKeyPress={(e) => e.key === 'Enter' && searchStudent()}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={searchStudent}
                disabled={loading}
                sx={{
                  height: '56px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                  }
                }}
              >
                <Search sx={{ mr: 1 }} />
                Search
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Student Info */}
        {studentInfo && (
          <Paper
            sx={{
              p: 3,
              mb: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Student Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">Student ID</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {studentInfo.student_id}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {studentInfo.user?.name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">Class</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {studentInfo.class?.name} - {studentInfo.class?.section}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">Total Paid</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#10b981' }}>
                  Rs. {getTotalPaid().toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Payment History Table */}
        {payments.length > 0 && (
          <Paper
            sx={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Payment Records ({payments.length})
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell><strong>Week</strong></TableCell>
                    <TableCell><strong>Amount</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Paid Date</strong></TableCell>
                    <TableCell><strong>Payment Method</strong></TableCell>
                    <TableCell><strong>Collected By</strong></TableCell>
                    <TableCell><strong>Remarks</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((payment) => (
                      <TableRow key={payment.id} hover>
                        <TableCell>
                          {new Date(payment.week_start_date).toLocaleDateString()} -{' '}
                          {new Date(payment.week_end_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>Rs. {parseFloat(payment.amount).toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip
                            label={payment.payment_status}
                            color={
                              payment.payment_status === 'paid'
                                ? 'success'
                                : payment.payment_status === 'overdue'
                                ? 'error'
                                : 'warning'
                            }
                            size="small"
                            icon={payment.payment_status === 'paid' ? <CheckCircle /> : undefined}
                          />
                        </TableCell>
                        <TableCell>
                          {payment.paid_date
                            ? new Date(payment.paid_date).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell>{payment.payment_method || '-'}</TableCell>
                        <TableCell>{payment.collector?.name || '-'}</TableCell>
                        <TableCell>{payment.remarks || '-'}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={payments.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default PaymentHistory;
